import { useQuery } from "@tanstack/react-query";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink } from "lucide-react";

interface UsageWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mediaId: string;
  onConfirm: () => void;
}

export const UsageWarningDialog = ({ open, onOpenChange, mediaId, onConfirm }: UsageWarningDialogProps) => {
  const { data: usages } = useQuery({
    queryKey: ['media-usage', mediaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_usage')
        .select(`
          *,
          blog_posts (
            id,
            title,
            slug
          )
        `)
        .eq('media_id', mediaId);
      
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const hasUsages = usages && usages.length > 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {hasUsages ? 'Image In Use' : 'Delete Image?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {hasUsages ? (
              <div className="space-y-3">
                <p>This image is currently being used in {usages.length} blog post(s):</p>
                <ul className="space-y-2">
                  {usages.map((usage: any) => (
                    <li key={usage.id} className="flex items-center gap-2 text-sm">
                      <ExternalLink className="h-4 w-4" />
                      <a 
                        href={`/blog/${usage.blog_posts.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {usage.blog_posts.title}
                      </a>
                      <span className="text-muted-foreground">
                        ({usage.usage_type.replace('_', ' ')})
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-destructive font-medium">
                  Deleting this image will break these blog posts. Are you sure?
                </p>
              </div>
            ) : (
              'This action cannot be undone. The image will be permanently deleted.'
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="bg-destructive hover:bg-destructive/90"
          >
            Delete Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
